import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import { BRAND, SERVICE_AREAS, SERVICES } from '@/data/site';

export default function Footer() {
  return (
    <footer className="bg-ohana-primary text-ohana-bg" data-testid="site-footer">
      <div className="container-ohana py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/ohana/logo.png"
                alt="Ohana Property & Transition Services LLC"
                className="h-11 w-11 rounded-full object-contain ring-1 ring-white/20"
                data-testid="footer-logo-image"
              />
              <span className="font-serif text-2xl">{BRAND.name}</span>
            </div>
            <p className="mt-5 text-ohana-bg/70 leading-relaxed text-sm max-w-sm">
              Fast, reliable property cleanouts serving {BRAND.serviceArea}. Family-rooted, donation-conscious, and built to show up when it counts.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-3 text-ohana-bg/80 hover:text-white transition"
                data-testid="footer-email-link"
              >
                <Mail className="h-4 w-4 text-ohana-accent" />
                {BRAND.email}
              </a>
              <a
                href={`tel:${BRAND.phoneTel}`}
                className="flex items-center gap-3 text-ohana-bg/80 hover:text-white transition"
                data-testid="footer-phone-link"
              >
                <Phone className="h-4 w-4 text-ohana-accent" />
                {BRAND.phone}
              </a>
              <a
                href={`sms:${BRAND.phoneSms}`}
                className="flex items-center gap-3 text-ohana-bg/80 hover:text-white transition text-xs"
                data-testid="footer-sms-link"
              >
                <span className="h-4 w-4" />
                Text us at {BRAND.phone}
              </a>
              <div className="flex items-center gap-3 text-ohana-bg/80">
                <MapPin className="h-4 w-4 text-ohana-accent" />
                {BRAND.serviceArea}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-ohana-accent">Services</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="text-ohana-bg/80 hover:text-white transition"
                    data-testid={`footer-link-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-2">
            <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-ohana-accent">Company</h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/about" className="text-ohana-bg/80 hover:text-white" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/blog" className="text-ohana-bg/80 hover:text-white" data-testid="footer-link-blog">Blog</Link></li>
              <li><Link to="/hearth-project" className="text-ohana-bg/80 hover:text-white" data-testid="footer-link-hearth">Hearth Project</Link></li>
              <li><Link to="/real-estate-partners" className="text-ohana-bg/80 hover:text-white" data-testid="footer-link-partners">For Investors</Link></li>
              <li><Link to="/contact" className="text-ohana-bg/80 hover:text-white" data-testid="footer-link-contact">Contact / Quote</Link></li>
            </ul>
          </div>

          {/* Service Areas */}
          <div className="md:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-ohana-accent">Service Areas</h4>
            <ul className="mt-5 grid grid-cols-2 gap-y-3 gap-x-4 text-sm text-ohana-bg/80">
              {SERVICE_AREAS.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col gap-4">
          <p className="text-xs text-ohana-bg/55 leading-relaxed" data-testid="footer-acceptance-policy">
            Ohana Property Services accepts standard nonhazardous cleanout items including furniture,
            household junk, move-out debris, estate cleanout items, and donation-eligible household
            goods with written permission. Ohana Property Services does not accept hazardous waste,
            medical waste, needles or sharps, chemicals, asbestos-containing materials, or regulated
            waste unless handled by a properly licensed partner.
          </p>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-ohana-bg/55 max-w-3xl leading-relaxed" data-testid="footer-disclaimer">
              {BRAND.disclaimer}
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ohana-bg/70">
              <Link to="/terms" className="hover:text-white transition" data-testid="footer-link-terms">
                Terms of Service
              </Link>
              <span aria-hidden="true" className="text-ohana-bg/30">·</span>
              <Link to="/privacy" className="hover:text-white transition" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
              <span aria-hidden="true" className="text-ohana-bg/30">·</span>
              <span className="text-ohana-bg/55">© {new Date().getFullYear()} {BRAND.name}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
